import { Theme, css } from '@emotion/react';
import { Help } from '@mui/icons-material';
import { Skeleton, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useTranslation } from '@scorenco/core';

import { ClubLogo, Score } from '../../components';
import { T_KEYS } from '../../translations';
import { normalizeEvent } from '../EventCard';

const styleClubhouse = {
  root: (theme: Theme) => css`
    .event-card_teams_container {
      background-color: ${theme.vars.palette.background.paper};
      box-shadow: inset 0px 0px 0px 2px ${theme.vars.palette.background.paper2};
      border-radius: 8px;
      overflow: hidden;
      width: max-content;
      max-width: 25rem;
      min-width: 25rem;
      min-height: 8rem;
    }

    .event-card_teams {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      width: 100%;
      border-left: 0.625rem solid ${theme.vars.palette.primary.main};
      padding: 1rem 0 1rem 1.5rem;
    }

    .event-card_team-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 20%;
    }

    .event-card_header_text {
      text-align: center;
      margin-bottom: 1rem;
    }

    .event-card_footer {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 1rem;
      margin-left: 1rem;
      margin-right: 1rem;
      padding: 0 1.5rem;

      &.event-card_footer-live {
        position: relative;
        background-color: #cc062a15;

        &::before {
          position: absolute;
          left: 0.5rem;
          content: '';
          height: 0.5rem;
          width: 0.5rem;
          margin-right: 0.25rem;
          background-color: #cc062a;
          border-radius: 9999px;
          display: inline-block;
        }
      }
    }

    .event-card_footer_text {
      font-size: 0.75rem;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .event_card_give-score {
      text-align: center;
      font-size: 10px;
      font-style: italic;
    }

    .event-card_scores {
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
      align-items: center;
      font-size: 2rem;
      font-weight: bold;
      padding-right: 1.5rem;
    }

    .event-card_tab_scores {
      display: flex;
      flex-direction: column;
      justify-content: center !important;
      align-items: center;
      gap: 0.8rem;
    }

    .event-card_team-away {
      text-align: center;
    }

    .event-card_logo {
      max-width: 2.25rem;
      height: 2.75rem;
    }

    .event-card_team {
      flex: 1;
      line-height: 1.2;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      min-width: 10rem;
      text-align: left;
      max-width: 10rem;
    }

    .event-card_date {
      display: flex;
      justify-content: center;
      border-left: 0.625rem solid ${theme.vars.palette.primary.main};
      padding: 0.3rem 0;
      font-size: 1rem;
      background: ${theme.vars.palette.background.paper2};
      width: 100%;
      border-radius: 0;
    }

    .event-card_date_to_come {
      background-color: ${theme.vars.palette.background.paper2};
      font-size: 1.2rem;
      font-weight: 600;
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      width: 6.5rem;
      height: 5rem;
    }

    .event-card_scoreboard {
      display: flex;
      &.is-winner {
        color: ${theme.vars.palette.secondary.main};
    },
    }

    .event-card_tab {
      display: flex;
      justify-content: center;
      width: 100%;
      border-left: 0.625rem solid ${theme.vars.palette.primary.main};
    }
  `,
};

export const EventPhaseCard = ({
  game,
  tournament,
  ...rest
}: {
  game: any;
  tournament: any;
}) => {
  const e = normalizeEvent(game);
  const { t } = useTranslation();

  let scoreboard;
  let shootout_scoreboard;
  let set_tab_home_score_style;
  let set_tab_away_score_style;

  if (game?.teams?.[0]?.data?.shootout > game?.teams?.[1]?.data?.shootout) {
    set_tab_home_score_style = (theme: Theme) => css`
      color: ${theme.vars.palette.secondary.main};
      font-weight: bold;
    `;
  } else if (
    game?.teams?.[0]?.data?.shootout < game?.teams?.[1]?.data?.shootout
  ) {
    set_tab_away_score_style = (theme: Theme) => css`
      color: ${theme.vars.palette.secondary.main};
      font-weight: bold;
    `;
  }

  if (
    (game?.teams?.[0]?.data?.shootout as number | undefined) !== null &&
    (game?.teams?.[0]?.data?.shootout as number | undefined) !== null
  ) {
    shootout_scoreboard = (
      <div className="event-card_scoreboard">
        <div className="event-card_tab_scores">
          <Typography
            css={set_tab_home_score_style}
            sx={{ fontSize: '1rem' }}
          >{`(${game?.teams?.[0]?.data?.shootout})`}</Typography>
          <Typography>TAB</Typography>
          <Typography
            css={set_tab_away_score_style}
            sx={{ fontSize: '1rem' }}
          >{`(${game?.teams?.[1]?.data?.shootout})`}</Typography>
        </div>
      </div>
    );
  }
  if (e.status === 'to_come') {
    scoreboard = (
      <div
        css={css`
          height: auto;
          margin: auto;
        `}
      >
        {tournament?.require_game_validation &&
        game?.is_validated_by_club === false ? (
          <div
            className="event_card_give-score"
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '0.5rem',
              borderRadius: '0.5rem',
              padding: '0.5rem',
            }}
          >
            <Help sx={{ fontSize: '0.75rem' }} />
            Match en attente de confirmation
          </div>
        ) : (
          <div className="event-card_date_to_come">
            <div>{e.date}</div>
            <div>{e.hour}</div>
          </div>
        )}
      </div>
    );
  } else if (e.status === 'postponed') {
    scoreboard = (
      <div
        css={css`
          height: auto;
          margin: auto;
        `}
      >
        <div className="event_card_give-score">{t(T_KEYS.EVENT_POSTPONED)}</div>
      </div>
    );
  } else if (e.status === 'live' && !e.isStarted) {
    scoreboard = (
      <div
        className="event_card_give-score"
        dangerouslySetInnerHTML={{
          __html: t(T_KEYS.EVENT_CARD_GIVE_SCORE) as string,
        }}
      />
    );
  } else {
    scoreboard = (
      <div className="event-card_scoreboard">
        <div className="event-card_scores">
          <Score
            score={(e.teams?.[0]?.score as number | undefined) ?? 0}
            opponentScore={(e.teams?.[1]?.score as number | undefined) ?? 0}
            isWinner={
              ((e.status === 'finish' || e.status === 'withdrawal') &&
                (e.teams[0]?.score as number) >
                  (e.teams[1]?.score as number)) ||
              (game?.teams?.[1]?.data?.shootout as number) <
                (game?.teams?.[0]?.data?.shootout as number)
            }
            sx={{ width: '2.5rem', height: '3rem' }}
          />
          <Score
            score={(e.teams?.[1]?.score as number | undefined) ?? 0}
            opponentScore={(e.teams?.[0]?.score as number | undefined) ?? 0}
            isWinner={
              ((e.status === 'finish' || e.status === 'withdrawal') &&
                (e.teams[0]?.score as number) <
                  (e.teams[1]?.score as number)) ||
              (game?.teams?.[1]?.data?.shootout as number) >
                (game?.teams?.[0]?.data?.shootout as number)
            }
            sx={{ width: '2.5rem', height: '3rem' }}
          />
        </div>
      </div>
    );
  }

  return (
    <Box
      css={styleClubhouse.root}
      {...rest}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        zIndex: 2,
      }}
    >
      <Box className="event-card_teams_container">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box className="event-card_teams">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                gap="1.5rem"
              >
                <ClubLogo
                  className="event-card_logo"
                  logo={game?.teams?.[0]?.logo}
                  name={game?.teams?.[0]?.name_in_competition}
                  shieldColor="home"
                  width={64}
                  height={64}
                />
                <Stack className="event-card_team">
                  {game?.teams?.[0]?.name_in_competition || <Skeleton />}
                </Stack>
              </Stack>
            </Box>
            <Box className="event-card_teams">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                gap="1.5rem"
              >
                <ClubLogo
                  className="event-card_logo"
                  logo={game?.teams?.[1]?.logo}
                  name={game?.teams?.[1]?.name_in_competition}
                  shieldColor="home"
                  width={64}
                  height={64}
                />
                <Stack className="event-card_team">
                  {game?.teams?.[1]?.name_in_competition || <Skeleton />}
                </Stack>
              </Stack>
            </Box>
          </Box>
          {game?.teams?.[0]?.data?.shootout != null && shootout_scoreboard}
          {scoreboard}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: 1,
          }}
        >
          <Box className="event-card_date">
            {[
              game?.place,
              game?.status !== 'to_come' &&
                new Date(game?.date).toLocaleDateString([], {
                  month: 'short',
                  day: '2-digit',
                }),
              game?.status === 'withdrawal' && 'Forfait',
            ]
              .filter(Boolean)
              .join(' - ')}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
